import { useState } from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'
import ConditionEditor, { describeCondition } from '../../components/ConditionEditor'
import type { FormDefinition, FormPage } from '../../types'
import { otherPagesFields } from './model'

interface Props {
    def: FormDefinition
    selectedId: string | null
    onSelect: (id: string) => void
    onAdd: () => void
    onUpdate: (id: string, patch: Partial<FormPage>) => void
    onRemove: (id: string) => void
    onMove: (id: string, delta: number) => void
}

export default function PageList({ def, selectedId, onSelect, onAdd, onUpdate, onRemove, onMove }: Props) {
    const [confirm, setConfirm] = useState<FormPage | null>(null)
    const selected = def.pages.find((p) => p.id === selectedId)
    const others = selected ? otherPagesFields(def, selected.id) : []

    return (
        <div className="flex flex-col gap-4">
            <Card size="sm">
                <CardHeader>
                    <CardTitle>Pages</CardTitle>
                    <CardDescription>Respondents move through active pages in this order.</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                    <ul className="flex flex-col gap-1.5" aria-label="Pages">
                        {def.pages.map((p, i) => (
                            <li key={p.id} className={cn('flex items-center gap-1 rounded-lg border p-1', p.id === selectedId ? 'border-primary bg-primary/5' : 'border-border')}>
                                <button
                                    type="button"
                                    aria-current={p.id === selectedId}
                                    onClick={() => onSelect(p.id)}
                                    className="flex min-w-0 flex-1 flex-col rounded-md px-2 py-1 text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50"
                                >
                                    <span className="truncate text-sm font-medium">{i + 1}. {p.title || 'Untitled page'}</span>
                                    <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                        {p.fields.length} field{p.fields.length === 1 ? '' : 's'}
                                        {p.activeWhen && <Badge variant="outline">conditional</Badge>}
                                    </span>
                                </button>
                                <Button variant="ghost" size="icon-xs" aria-label={`Move page ${p.title || i + 1} up`} disabled={i === 0} onClick={() => onMove(p.id, -1)}><ArrowUp /></Button>
                                <Button variant="ghost" size="icon-xs" aria-label={`Move page ${p.title || i + 1} down`} disabled={i === def.pages.length - 1} onClick={() => onMove(p.id, 1)}><ArrowDown /></Button>
                            </li>
                        ))}
                    </ul>
                    <Button variant="outline" size="sm" onClick={onAdd}><Plus data-icon="inline-start" /> Add page</Button>
                </CardContent>
            </Card>

            {selected && (
                <Card size="sm">
                    <CardHeader>
                        <CardTitle>Page settings</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <FieldGroup>
                            <Field>
                                <FieldLabel htmlFor="pg-title">Title</FieldLabel>
                                <Input id="pg-title" value={selected.title} onChange={(e) => onUpdate(selected.id, { title: e.target.value })} />
                            </Field>
                            <Field>
                                <FieldLabel htmlFor="pg-desc">Description</FieldLabel>
                                <Textarea id="pg-desc" rows={2} value={selected.description ?? ''} onChange={(e) => onUpdate(selected.id, { description: e.target.value || undefined })} />
                            </Field>
                            <Field>
                                <FieldLabel>Show this page when</FieldLabel>
                                {others.length === 0 && !selected.activeWhen ? (
                                    <FieldDescription>Other pages have no questions to depend on yet. Always shown.</FieldDescription>
                                ) : (
                                    <>
                                        <ConditionEditor value={selected.activeWhen} onChange={(c) => onUpdate(selected.id, { activeWhen: c })} fields={others} emptyText="Always shown" />
                                        {selected.activeWhen && <FieldDescription>{describeCondition(selected.activeWhen, others)}</FieldDescription>}
                                    </>
                                )}
                            </Field>
                            <Button variant="destructive" size="sm" onClick={() => setConfirm(selected)}>
                                <Trash2 data-icon="inline-start" /> Delete page
                            </Button>
                        </FieldGroup>
                    </CardContent>
                </Card>
            )}

            <Dialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null) }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete {confirm?.title || 'this page'}?</DialogTitle>
                        <DialogDescription>
                            {confirm && confirm.fields.length > 0
                                ? `This also deletes its ${confirm.fields.length} field${confirm.fields.length === 1 ? '' : 's'}. Rules or conditions that use them will need fixing.`
                                : 'This page is empty.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirm(null)}>Cancel</Button>
                        <Button variant="destructive" onClick={() => { if (confirm) onRemove(confirm.id); setConfirm(null) }}>Delete page</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
